/**
 * The brand profile context object and its consumer hook, deliberately split from the
 * provider in `BrandProfileContext.tsx`.
 *
 * The provider needs Supabase, React Query and the router to resolve which brands a family
 * belongs to. Consumers need none of that — they read one string. Keeping them in one module
 * would make every file that reads a label import `lib/supabase`, which **throws at import
 * time** when the Supabase env vars are absent, so a label-only unit test would have to mock
 * a client it never calls. This file pulls in nothing at runtime but React and the brand
 * keys: the one import from `utils/analytics` is `import type`, which the compiler erases,
 * so the Supabase client that module holds is never reached from here. Keep it that way —
 * a value import from there would reintroduce exactly the failure this split avoids.
 *
 * The default value matters: surfaces that render outside `BrandProfileProvider` — the admin
 * and teacher portals, and most component tests — get the default brand rather than an error.
 * Brand is presentation scope, so "nobody told me" has a sensible answer, unlike auth.
 */

import { createContext, useContext } from 'react';
import { DEFAULT_BRAND_KEY, type BrandKey } from '../config/brands';
import type { BrandSwitchSource } from '../utils/analytics';

export interface BrandProfileContextValue {
    /** The catalog being browsed. Never null — falls back to `DEFAULT_BRAND_KEY`. */
    activeBrand: BrandKey;
    /**
     * Switch profiles and remember the choice for this account.
     *
     * `source` only labels the analytics event and defaults to the header switcher, since that
     * is where all but one caller is. The sign-up picker passes `signup`; the portal's own
     * passive routes (`link`, `landing`) are emitted inside the provider.
     */
    setActiveBrand: (brand: BrandKey, source?: BrandSwitchSource) => void;
    /** Brands this family already has enrollments in. Empty for a brand-new account. */
    memberBrands: readonly BrandKey[];
    /** Brands the switcher offers. Every known brand — a parent can always browse the other. */
    availableBrands: readonly BrandKey[];
    /**
     * Whether `activeBrand` is a choice this account actually made (switcher, sign-up picker
     * or `?brand=`), as opposed to one derived from their enrollments or the default.
     *
     * Sign-up reads it to avoid overriding a parent who has already decided.
     */
    hasChosenBrand: boolean;
    /**
     * Whether this surface is inside the parent portal's `BrandProfileProvider`.
     *
     * Always true there. False only from the context default — the admin and teacher portals
     * and component tests with no provider — where the feature is inert: `activeBrand` is the
     * default, `availableBrands` holds only that brand, and `setActiveBrand` does nothing, so
     * the switcher, the sign-up picker, the calendar view and the brand-scoped catalog stay
     * out of surfaces that span every brand.
     */
    isMultiBrand: boolean;
    /** Still resolving `memberBrands`; the active brand is already usable. */
    isLoading: boolean;
    /**
     * Report the brand of the thing a page is about — `programs.brand_key` of the invoice,
     * enrollment or program on screen. Call it with the raw value; undefined while loading.
     *
     * Switches the profile to that brand **only on the page this visit landed on**, i.e. a link
     * followed from outside the portal (an invoice email, a shared program link). Anywhere else
     * it does nothing: a parent browsing inside the portal chose their profile, and opening a
     * row from a list that spans both brands is not a request to change it. Answers once per
     * landing, so a parent who switches away afterwards is not pulled back.
     *
     * Use it through `useLandingBrand`, not directly.
     */
    alignLandingBrand: (subjectBrand: string | null | undefined) => void;
}

export const BrandProfileContext = createContext<BrandProfileContextValue>({
    activeBrand: DEFAULT_BRAND_KEY,
    setActiveBrand: () => {},
    memberBrands: [],
    availableBrands: [DEFAULT_BRAND_KEY],
    hasChosenBrand: false,
    isMultiBrand: false,
    isLoading: false,
    alignLandingBrand: () => {},
});

export function useBrandProfile(): BrandProfileContextValue {
    return useContext(BrandProfileContext);
}
